import type { SellingPlan, Variant } from "@/hooks/usePurchasedProductDetail";
import {
  getPlanPricing,
  getSellingPlansFromVariant,
} from "@/lib/sellingPlanUtils";
import { monthsPerCycle } from "./subscription-pricing";
import type { CancelSubReasonId } from "shared/cancel-sub-reason";

/**
 * The "size-up bundle" retention offer for the price branch of the cancel chat.
 *
 * The idea, in one line: keep the customer's servings-per-month EXACTLY the same,
 * but move them to a bigger unit billed less often, so their real monthly spend
 * drops. It is the lead price save when it qualifies (see cancel-sub-reason
 * `leads`), and it is computed entirely client-side from the product detail —
 * there is no pricing endpoint (unlike the cross-sell swap). Prices come from the
 * variants' Subscribe & Save selling plans via `sellingPlanUtils`, so a figure is
 * never hand-typed.
 *
 * SCOPE — deliberately narrow (agreed 2026-07-16):
 *  - Rose Nectar only. The servings math is calibrated to its oz-based sizes and
 *    the "N servings | … / M-Pack" variant-title convention (pack count multiplies
 *    servings per delivery); other products don't share it.
 *  - SINGLE-LINE contracts only. Changing the interval re-cadences the WHOLE
 *    contract, so a size-up on one line would silently re-time its siblings.
 *  - Price roots only (budget_tight / price_vs_value). The caller gates this; the
 *    module just picks differently per root.
 *
 * FULFILLMENT NOTE: the cadence (N months) is applied via change-frequency, which
 * is independent of the selling plan — so the target price is the variant's
 * Subscribe & Save per-delivery price (one % discount across the group), NOT a
 * price tied to an N-month plan that may not exist. See CancelSubscriptionChat's
 * accept handler + the backend `applySizeUpBundle` controller.
 *
 * SHIPPING: current fee comes from the contract; the target fee must be
 * previewed via `previewSubscriptionShipping` (draftOrderCalculate) so free
 * shipping over $100 is reflected. Do not reuse current deliveryPrice for the
 * new cart.
 */

/** Longest interval (in whole months) we will stretch a delivery to. */
export const MAX_BUNDLE_INTERVAL_MONTHS = 3;

/** Titles that opt a product into the size-up bundle (Rose Nectar only for now). */
const ROSE_NECTAR_TITLE = /rose\s*nectar/i;

/**
 * Allowed Rose Nectar SKUs for the size-up ladder (current + target).
 * Advertised servings × pack — not delivery-total servings.
 *   32 servings 1-Pack → 64 servings 1-Pack → 64 servings 3-Pack
 */
const BUNDLE_ALLOWED: ReadonlyArray<{ servings: number; packs: number }> = [
  { servings: 32, packs: 1 },
  { servings: 64, packs: 1 },
  { servings: 64, packs: 3 },
];

/** Servings advertised on a variant title, e.g. "32 servings | 8oz / 1-Pack" → 32. */
export function parseServings(title: string | null | undefined): number | null {
  if (!title) return null;
  const m = title.match(/(\d+(?:\.\d+)?)\s*servings/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Pack count from a variant title, e.g. "64 servings | 16oz / 3-Pack" → 3.
 * Defaults to 1 when the title has no N-Pack token (or an unusable one).
 */
export function parsePackCount(title: string | null | undefined): number {
  if (!title) return 1;
  const m = title.match(/(\d+)\s*-?\s*packs?\b/i);
  if (!m) return 1;
  const n = Number.parseInt(m[1], 10);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

/**
 * True when the title is one of the three size-up ladder SKUs
 * (32 1-Pack, 64 1-Pack, 64 3-Pack). Other sizes (e.g. 96 servings) are ignored.
 */
export function isBundleEligibleVariant(
  title: string | null | undefined,
): boolean {
  const servings = parseServings(title);
  if (servings == null) return false;
  const packs = parsePackCount(title);
  return BUNDLE_ALLOWED.some(
    (a) => a.servings === servings && a.packs === packs,
  );
}

/**
 * Total servings in one delivery of this variant: servings × pack count.
 * "64 servings | 16oz / 3-Pack" → 192.
 */
export function totalServingsFromTitle(
  title: string | null | undefined,
): number | null {
  const servings = parseServings(title);
  if (servings == null) return null;
  return servings * parsePackCount(title);
}

/** The size portion of a variant title for display, e.g. "8oz / 1-Pack". */
export function parseSizeLabel(title: string | null | undefined): string {
  if (!title) return "";
  // Everything after the first "|" is the human size ("8oz / 1-Pack"); fall back
  // to the whole title when the convention isn't followed.
  const idx = title.indexOf("|");
  return (idx >= 0 ? title.slice(idx + 1) : title).trim();
}

/**
 * Promo percentage already on the subscription (e.g. STAY15 → 15), or null.
 * Folded into {@link SizeUpBundle.targetNetPrice} for the quote and for the
 * Stay AI swap price (`isDiscounted: true` = use that exact amount).
 */
export function subscriptionPromoPercent(
  discounts: { percentage?: number | null }[] | null | undefined,
): number | null {
  for (const d of discounts ?? []) {
    if (d?.percentage != null && Number.isFinite(d.percentage) && d.percentage > 0) {
      return d.percentage;
    }
  }
  return null;
}

/** Apply a promo percentage to a catalog amount (rounded to cents). */
export function applyPromoPercent(
  amount: number,
  percent: number | null | undefined,
): number {
  if (percent == null || !(percent > 0)) return amount;
  return round2(amount * (1 - percent / 100));
}

/**
 * The per-delivery SUBSCRIPTION price of a variant — its Subscribe & Save price.
 * Subscribe & Save is a single percentage off the variant list price applied
 * across the plan group, so the representative (first) plan's adjustment is the
 * per-delivery price at any cadence; returns null when the variant isn't
 * subscribable or carries no usable adjustment.
 *
 * Important: this is the selling-plan price on the TARGET VARIANT itself (e.g.
 * the 3-Pack SKU's Subscribe & Save amount), not a price looked up from an
 * N-month delivery plan. The new cadence is applied separately via
 * change-frequency after the variant swap.
 */
export function variantSubscriptionUnitPrice(
  variant: Variant,
): number | null {
  if (variant.is_subscription === false) return null;
  const plans: SellingPlan[] = getSellingPlansFromVariant(variant);
  if (plans.length === 0) return null;
  // The lowest per-delivery price across plans — defends against a stray plan with
  // no/percentage-zero adjustment inflating the figure.
  let best: number | null = null;
  for (const plan of plans) {
    const { discountedPrice } = getPlanPricing(variant.price, plan);
    if (discountedPrice != null && Number.isFinite(discountedPrice)) {
      best = best == null ? discountedPrice : Math.min(best, discountedPrice);
    }
  }
  return best != null && best > 0 ? best : null;
}

export interface SizeUpBundle {
  /** Shopify variant id to swap the line to. */
  targetVariantId: number;
  targetVariantTitle: string;
  /** Human size, e.g. "16oz / 1-Pack". */
  newSizeLabel: string;
  /** New billing interval, whole months (2 or 3). */
  newIntervalMonths: number;
  /**
   * Per-delivery Subscribe & Save catalog price of the target variant (after
   * selling plan, before any STAY15-style promo). Stored locally as `unit_price`
   * on accept; Stay AI receives {@link targetNetPrice} instead.
   */
  targetUnitPrice: number;
  /**
   * Exact per-delivery charge after the subscription's promo % — what we send
   * to Stay AI on accept (`isDiscounted: true` = use this amount as-is).
   */
  targetNetPrice: number;
  quantity: number;
  /** Cost per month today. Includes shipping only when `shippingIncluded`. */
  currentMonthly: number;
  /** Cost per month on the bundle. Includes shipping only when `shippingIncluded`. */
  newMonthly: number;
  /** currentMonthly − newMonthly, strictly positive (rounded to cents). */
  savingPerMonth: number;
  /**
   * Whether both current and target shipping were known and folded into the
   * monthly figures (target from shipping-preview, not reused current).
   */
  shippingIncluded: boolean;
  /**
   * The share of the monthly saving that comes from shipping
   * (currentShip ÷ old months − targetShip ÷ new months). 0 when shipping
   * wasn't known — see `shippingIncluded`.
   */
  shippingSavingPerMonth: number;
  /** Servings/month held constant across the change. */
  servingsPerMonth: number;
  /** Servings the target unit delivers (internal math). */
  newServings: number;
  /** Packs in one delivery of the target (for customer-facing copy). */
  newPackCount: number;
  /** Packs/month today (for "Right now you're getting N pack(s) a month"). */
  currentPacksPerMonth: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
/** Tolerance for "whole number of servings-per-month divides the target". */
const EPS = 1e-6;

export interface ComputeSizeUpBundleArgs {
  productTitle: string;
  /** Whether THIS is the only line on the contract (interval change is contract-wide). */
  isSingleLine: boolean;
  /** Current billing cadence (normalized), or null when unknown. */
  cadence: { interval: number; intervalUnit: string } | null;
  currentVariantId: number | null;
  currentVariantTitle: string | null;
  /** All variants of the product (from usePurchasedProductDetail). */
  variants: Variant[];
  /** What the (single-line) contract bills per delivery today — the displayed total. */
  contractPerDeliveryTotal: number | null;
  /** Per-delivery shipping on the current contract, or null when unknown. */
  currentShippingCost: number | null;
  /**
   * Previewed per-delivery shipping for the TARGET variant (from
   * shipping-preview / draftOrderCalculate). Null when not yet fetched or
   * unavailable — then shipping is omitted from both sides.
   */
  targetShippingCost: number | null;
  /**
   * Promo % already on the subscription (e.g. 15 for STAY15). Folded into
   * `targetNetPrice` for the quote and for the Stay AI swap charge.
   */
  discountPercent: number | null;
  /** The recorded price root; decides which qualifying bundle we pick. */
  subReason: CancelSubReasonId | null;
}

/**
 * Compute the best size-up bundle, or null when none qualifies (the caller then
 * falls back to today's levers — the bundle is never forced).
 */
export function computeSizeUpBundle(
  args: ComputeSizeUpBundleArgs,
): SizeUpBundle | null {
  const {
    productTitle,
    isSingleLine,
    cadence,
    currentVariantId,
    currentVariantTitle,
    variants,
    contractPerDeliveryTotal,
    currentShippingCost,
    targetShippingCost,
    discountPercent,
    subReason,
  } = args;

  if (!isSingleLine) return null;
  if (!ROSE_NECTAR_TITLE.test(productTitle)) return null;
  if (currentVariantId == null) return null;

  const currentMonths = monthsPerCycle(cadence);
  if (currentMonths == null || currentMonths <= 0) return null;
  if (contractPerDeliveryTotal == null || contractPerDeliveryTotal <= 0) {
    return null;
  }

  // Only the three ladder SKUs participate (as current or as target).
  if (!isBundleEligibleVariant(currentVariantTitle)) return null;

  const currentServings = totalServingsFromTitle(currentVariantTitle);
  if (currentServings == null) return null;

  // Servings/month we must hold constant.
  const servingsPerMonth = currentServings / currentMonths;
  if (!(servingsPerMonth > 0)) return null;

  const currentPacks = parsePackCount(currentVariantTitle);
  const currentPacksPerMonth = currentPacks / currentMonths;

  // Fold shipping only when BOTH sides are known. Target must come from the
  // shipping-preview endpoint (free shipping over $100 etc.) — never reuse the
  // current fee for the new cart.
  const shippingIncluded =
    currentShippingCost != null &&
    Number.isFinite(currentShippingCost) &&
    targetShippingCost != null &&
    Number.isFinite(targetShippingCost);
  const currentShip = shippingIncluded ? currentShippingCost : 0;
  const targetShip = shippingIncluded ? targetShippingCost : 0;

  const currentMonthly =
    (contractPerDeliveryTotal + currentShip) / currentMonths;

  interface Candidate {
    variant: Variant;
    intervalMonths: number;
    targetUnitPrice: number;
    targetNetPrice: number;
    newMonthly: number;
    savingPerMonth: number;
    servings: number;
    packCount: number;
  }

  const candidates: Candidate[] = [];
  for (const variant of variants) {
    if (variant.shopify_id === currentVariantId) continue;
    if (!isBundleEligibleVariant(variant.title)) continue;
    const servings = totalServingsFromTitle(variant.title);
    if (servings == null) continue;
    // Never LESS product: the target must deliver at least as many servings.
    if (servings < currentServings) continue;

    // The whole-number interval (months) that holds servings/month EXACTLY equal.
    const interval = servings / servingsPerMonth;
    const rounded = Math.round(interval);
    if (Math.abs(interval - rounded) > EPS) continue; // not exactly equal
    if (rounded < 2 || rounded > MAX_BUNDLE_INTERVAL_MONTHS) continue; // longer, ≤ 3mo

    // Per-delivery Subscribe & Save price on THIS variant (lowest across its
    // selling plans). Cadence is applied separately via change-frequency — we
    // do not require an N-month selling plan to exist on the target.
    const targetUnitPrice = variantSubscriptionUnitPrice(variant);
    if (targetUnitPrice == null) continue;

    const targetNetPrice = applyPromoPercent(targetUnitPrice, discountPercent);
    const newMonthly = (targetNetPrice + targetShip) / rounded;
    const savingPerMonth = round2(currentMonthly - newMonthly);
    if (savingPerMonth <= 0) continue; // must strictly lower cost/month

    candidates.push({
      variant,
      intervalMonths: rounded,
      targetUnitPrice,
      targetNetPrice,
      newMonthly,
      savingPerMonth,
      servings,
      packCount: parsePackCount(variant.title),
    });
  }

  if (candidates.length === 0) return null;

  // budget_tight → the SMALLEST upfront upsize that still lowers cost/month (they
  // can't spare much now); every other price root → the CHEAPEST cost/month.
  // Upfront compares the NET the customer pays (promo folded in).
  const pick =
    subReason === "budget_tight"
      ? [...candidates].sort(
          (a, b) =>
            a.targetNetPrice - b.targetNetPrice ||
            a.newMonthly - b.newMonthly ||
            a.intervalMonths - b.intervalMonths,
        )[0]
      : [...candidates].sort(
          (a, b) =>
            a.newMonthly - b.newMonthly ||
            a.targetNetPrice - b.targetNetPrice ||
            a.intervalMonths - b.intervalMonths,
        )[0];

  return {
    targetVariantId: pick.variant.shopify_id,
    targetVariantTitle: pick.variant.title,
    newSizeLabel: parseSizeLabel(pick.variant.title),
    newIntervalMonths: pick.intervalMonths,
    targetUnitPrice: round2(pick.targetUnitPrice),
    targetNetPrice: round2(pick.targetNetPrice),
    quantity: 1,
    currentMonthly: round2(currentMonthly),
    newMonthly: round2(pick.newMonthly),
    savingPerMonth: pick.savingPerMonth,
    shippingIncluded,
    shippingSavingPerMonth: shippingIncluded
      ? round2(currentShip / currentMonths - targetShip / pick.intervalMonths)
      : 0,
    servingsPerMonth,
    newServings: pick.servings,
    newPackCount: pick.packCount,
    currentPacksPerMonth,
  };
}
